import React, { useEffect, useState, useRef, useCallback } from "react";
import "../../Styles/Assign/assign.css";
import Modal from "./Model";
import axiosInstance from "../../interceptor/axiosInstance";
import AssignBulk from "./AssignBulk/AssignBulk";
import SearchBarAssign from "./SearchBarAssign";
import AssignedList from "./AssignedList";
import { toast } from '../Library/SubMenus/MCQAssessment/toastUtils';

// Skeleton Loader Component
const SkeletonLoader = ({ pageSize }) => {
  return (
    <div className="assign-skeleton-loader">
      <div className="assign-skeleton-header">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="assign-skeleton-header-cell" 
            style={{ width: i === 2 ? '30%' : i === 3 ? '20%' : '15%' }} />
        ))}
      </div>
      {[...Array(pageSize)].map((_, rowIndex) => (
        <div key={rowIndex} className="assign-skeleton-row">
          <div className="assign-skeleton-cell" style={{ width: '15%' }} />
          <div className="assign-skeleton-cell" style={{ width: '15%' }} />
          <div className="assign-skeleton-cell" style={{ width: '30%' }} />
          <div className="assign-skeleton-cell" style={{ width: '20%' }} />
        </div>
      ))}
    </div>
  );
};

// Empty State Component
const EmptyState = ({ searchTerm, onClearSearch }) => {
  const hasSearch = searchTerm && searchTerm.length > 0;
  
  return (
    <div className="assign-empty-state">
      <div className="assign-empty-state-icon">
        {hasSearch ? '🔍' : '👥'}
      </div>
      <h3>{hasSearch ? 'No Users Found' : 'No Users Yet'}</h3>
      <p>
        {hasSearch
          ? `No users match your search criteria "${searchTerm}". Try adjusting your search term.`
          : 'There are no users in the system yet. Users will appear here once they are added.'}
      </p>
      {hasSearch && (
        <button className="assign-empty-state-btn" onClick={onClearSearch}>
          Clear Search
        </button>
      )}
    </div>
  );
};

const Assign = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectAssignUser, setSelectAssignUser] = useState(null);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [jumpToPage, setJumpToPage] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // Refs for abort controller
  const abortControllerRef = useRef(null);

  const fetchData = useCallback(async (page = 1, search = '', size = pageSize) => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const isSearching = search !== '';
    if (isSearching) {
      setSearching(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axiosInstance.get(
        `accounts/users/?page=${page}&page_size=${size}&username=${search}`,
        {
          signal: abortControllerRef.current.signal
        }
      );
      const { pagination, results } = response.data.data;

      setUsers(results);
      setCount(pagination.count);
      setTotalPages(Math.ceil(pagination.count / size));
      setCurrentPage(page);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        toast.error('Fetch Failed', 'Failed to load users. Please try again.');
      }
    } finally {
      setLoading(false);
      setSearching(false);
    }
  }, [pageSize]);

  // Fetch data when the page size changes
  useEffect(() => {
    fetchData(1, searchTerm, pageSize);
  }, [pageSize, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  

  // Sorting function
  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key: columnKey, direction });

    const sortedUsers = [...users].sort((a, b) => {
      if (a[columnKey] < b[columnKey]) return direction === "asc" ? -1 : 1;
      if (a[columnKey] > b[columnKey]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setUsers(sortedUsers);
  };

  // Function to display correct sorting icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "⇅";
  };

  const handleChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    fetchData(1, '', pageSize);
  };

  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpToPage, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      fetchData(pageNum, searchTerm, pageSize);
      setJumpToPage('');
    } else {
      toast.warning('Invalid Page', `Please enter a page number between 1 and ${totalPages}`);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(newPage, searchTerm, pageSize);
    }
  };

  return (
    <div className="assign-container">
      <h2 className="assign-name-top">Assign User Series</h2>

      <div className="assign-top-button">
        <SearchBarAssign 
          setUsers={setUsers} 
          setLoading={setLoading}
          fetchData={fetchData}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          pageSize={pageSize}
        />
        <div className="assign-top-two-btn">
          <button onClick={() => setIsBulkAssignOpen(true)} className="assign-bulk-btn">
            Bulk Assign
          </button>
          <div className="assign-page-size-selector">
            <label htmlFor="pageSize">Show:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={handleChange}
              className="assign-page-size-select"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <SkeletonLoader pageSize={pageSize} />
      ) : users.length === 0 ? (
        <EmptyState searchTerm={searchTerm} onClearSearch={handleClearSearch} />
      ) : (
        <>
          <div className="assign-table-info">
            <span>Showing {users.length} of {count} users</span>
          </div>

          <div className="assign-table-wrapper">
            <table className="assign-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("first_name")} className="assign-sortable-header">
                    First Name {getSortIcon("first_name")}
                  </th>
                  <th onClick={() => handleSort("last_name")} className="assign-sortable-header">
                    Last Name {getSortIcon("last_name")}
                  </th>
                  <th onClick={() => handleSort("email")} className="assign-sortable-header">
                    Email {getSortIcon("email")}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.first_name}</td>
                    <td>{user.last_name}</td>
                    <td>{user.email}</td>
                    <td className="assign-user-btn">
                      <button onClick={() => setSelectedUser(user)} className="assign-edit-btn">
                        Edit
                      </button>
                      <button onClick={() => setSelectAssignUser(user)} className="assign-list-btn">
                        Assigned List
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Enhanced Pagination Controls */}
          <div className="assign-pagination-container">
            <div className="assign-pagination-info">
              Page {currentPage} of {totalPages} ({count} total users)
            </div>
            
            <div className="assign-pagination-controls">
              <button 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
                className="assign-pagination-btn"
                title="First Page"
              >
                « First
              </button>
              <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                className="assign-pagination-btn"
                title="Previous Page"
              >
                ‹ Previous
              </button>
              
              <div className="assign-pagination-jump">
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  placeholder={currentPage.toString()}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleJumpToPage()}
                  className="assign-jump-input"
                />
                <button 
                  onClick={handleJumpToPage}
                  className="assign-jump-btn"
                  title="Jump to page"
                >
                  Go
                </button>
              </div>

              <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage >= totalPages}
                className="assign-pagination-btn"
                title="Next Page"
              >
                Next ›
              </button>
              <button 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage >= totalPages}
                className="assign-pagination-btn"
                title="Last Page"
              >
                Last »
              </button>
            </div>
          </div>
        </>
      )}

      {selectedUser && <Modal user={selectedUser} onClose={() => setSelectedUser(null)} />}
      {selectAssignUser && <AssignedList user={selectAssignUser} onClose={() => setSelectAssignUser(null)} />}
      {isBulkAssignOpen && <AssignBulk onClose={() => setIsBulkAssignOpen(false)} />}
    </div>
  );
};

export default Assign;
